<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal px-4 py-3 md:px-6">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 class="list-page-title">签署流程</h1>
            <p class="mt-2 text-sm leading-6 text-slate-600">发起、跟踪和处理腾讯电子签流程，支持同步、催办、延期、解除、补充签署方和归档。</p>
          </div>
          <button type="button" class="btn-primary inline-flex items-center gap-2" @click="openCreate">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            发起流程
          </button>
        </div>
      </div>
    </template>

    <div class="space-y-4">
      <div class="grid gap-3 md:grid-cols-4">
        <div v-for="item in summaryCards" :key="item.label" class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p class="text-xs font-medium text-slate-500">{{ item.label }}</p>
          <p class="mt-2 text-2xl font-semibold text-slate-900">{{ item.value }}</p>
        </div>
      </div>

      <div class="list-page-table-panel">
        <PageFilterBar
          shell-class="filter-bar-shell is-embedded"
          body-class="filter-bar-body is-panel"
          :show-refresh="true"
          :refresh-loading="loading"
          @refresh="fetchList"
        >
          <div class="filter-bar-field is-wide relative">
            <input v-model="filters.keyword" class="form-search-input form-control-sm w-full" placeholder="搜索流程号、名称或业务编号" @keyup.enter="handleSearch" />
            <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input v-model="filters.bizType" class="form-input form-control-sm filter-bar-field" placeholder="业务类型" />
          <el-select v-model="filters.flowStatus" placeholder="请选择流程状态" class="form-select form-control-sm filter-bar-field">
            <el-option label="全部流程状态" value="" />
            <el-option v-for="option in flowStatusOptions" :key="option.value" :label="option.label" :value="option.value" />
          </el-select>
          <el-select v-model="filters.archiveStatus" placeholder="请选择归档状态" class="form-select form-control-sm filter-bar-field">
            <el-option label="全部归档状态" value="" />
            <el-option label="未归档" value="NONE" />
            <el-option label="待归档" value="PENDING" />
            <el-option label="归档中" value="ARCHIVING" />
            <el-option label="已归档" value="SUCCESS" />
            <el-option label="归档失败" value="FAILED" />
          </el-select>
          <template #searchActions>
            <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
            <button type="button" class="btn-secondary" @click="handleReset">重置</button>
          </template>
        </PageFilterBar>

        <div class="data-table-shell border-none shadow-none">
          <div class="data-table-scroll">
            <table class="data-table text-sm">
              <thead class="data-table-head">
                <tr>
                  <th class="data-table-th text-left">流程</th>
                  <th class="data-table-th text-left">业务</th>
                  <th class="data-table-th text-left">模板</th>
                  <th class="data-table-th text-left">腾讯 FlowId</th>
                  <th class="data-table-th text-left">状态</th>
                  <th class="data-table-th text-left">归档</th>
                  <th class="data-table-th text-left">截止时间</th>
                  <th class="data-table-th is-center">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading" class="data-table-row"><td colspan="8" class="data-table-empty">正在加载流程...</td></tr>
                <tr v-else-if="!list.length" class="data-table-row"><td colspan="8" class="data-table-empty">暂无签署流程</td></tr>
                <tr v-for="item in list" v-else :key="item.id" class="data-table-row">
                  <td class="data-table-td min-w-[240px]">
                    <div class="data-table-cell-title">{{ item.flowName || item.subject || '-' }}</div>
                    <div class="data-table-cell-meta">{{ item.flowNo || item.id }}</div>
                  </td>
                  <td class="data-table-td whitespace-nowrap">
                    <div class="text-slate-900">{{ item.bizType || '-' }}</div>
                    <div class="text-xs text-slate-500">{{ item.bizNo || item.bizId || '-' }}</div>
                  </td>
                  <td class="data-table-td whitespace-nowrap">{{ item.templateName || item.templateCode || item.templateId || '-' }}</td>
                  <td class="data-table-td whitespace-nowrap font-mono text-xs text-slate-600">{{ blank(item.tencentFlowId) }}</td>
                  <td class="data-table-td whitespace-nowrap"><span :class="['status-badge', flowStatusClass(item.flowStatus || item.status)]">{{ flowStatusText(item.flowStatus || item.status) }}</span></td>
                  <td class="data-table-td whitespace-nowrap"><span :class="['status-badge', statusClassByResult(item.archiveStatus)]">{{ archiveStatusText(item.archiveStatus) }}</span></td>
                  <td class="data-table-td whitespace-nowrap text-slate-600">{{ formatDateTime(item.expireTime) || '-' }}</td>
                  <td class="data-table-td is-center whitespace-nowrap">
                    <div class="flex flex-wrap items-center justify-center gap-1">
                      <button class="table-action-button is-primary" @click="openDetail(item)">详情</button>
                      <button class="table-action-button is-success" @click="runSimpleAction(item, 'sync')">同步</button>
                      <button class="table-action-button is-teal" @click="runSimpleAction(item, 'components')">控件</button>
                      <button class="table-action-button is-indigo" @click="openOperation(item, 'deadline')">延期</button>
                      <button class="table-action-button" @click="openOperation(item, 'remind')">催办</button>
                      <button class="table-action-button" @click="openOperation(item, 'approvers')">补签署方</button>
                      <button class="table-action-button" @click="openOperation(item, 'forward')">转他人</button>
                      <button class="table-action-button is-purple" @click="runSimpleAction(item, 'archive')">归档</button>
                      <button class="table-action-button is-danger" @click="openOperation(item, 'cancel')">取消</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <AppPagination class="pagination-bridge" :pagination="pagination" :page-sizes="[10, 20, 50]" @page-change="handlePageChange" @size-change="handlePageSizeChange" />
      </div>
    </div>

    <el-drawer v-model="detailVisible" title="流程详情" size="760px">
      <div v-if="detail" class="space-y-5">
        <section class="rounded-2xl border border-slate-200 p-4">
          <h3 class="text-sm font-semibold text-slate-900">基础信息</h3>
          <dl class="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><dt class="text-slate-500">流程名称</dt><dd class="mt-1 text-slate-900">{{ detail.flowName || detail.subject || '-' }}</dd></div>
            <div><dt class="text-slate-500">流程号</dt><dd class="mt-1 text-slate-900">{{ detail.flowNo || detail.id }}</dd></div>
            <div><dt class="text-slate-500">腾讯 FlowId</dt><dd class="mt-1 font-mono text-xs">{{ blank(detail.tencentFlowId) }}</dd></div>
            <div><dt class="text-slate-500">失败原因</dt><dd class="mt-1 text-slate-900">{{ blank(detail.failReason) }}</dd></div>
          </dl>
        </section>
        <section class="rounded-2xl border border-slate-200 p-4">
          <h3 class="text-sm font-semibold text-slate-900">签署方</h3>
          <div class="mt-3 space-y-2">
            <div v-for="party in detailParties" :key="party.id || party.tencentRecipientId" class="rounded-xl bg-slate-50 p-3 text-sm">
              <div class="flex items-center justify-between gap-3">
                <div class="font-medium text-slate-900">{{ party.roleName || party.personName || party.organizationName || '-' }}</div>
                <span :class="['status-badge', statusClassByResult(party.partyStatus || party.status)]">{{ partyStatusText(party.partyStatus || party.status) }}</span>
              </div>
              <div class="mt-1 text-xs text-slate-500">{{ party.personMobile || '-' }} / RecipientId: {{ party.tencentRecipientId || '-' }}</div>
            </div>
            <p v-if="!detailParties.length" class="text-sm text-slate-500">暂无签署方信息</p>
          </div>
        </section>
        <section class="rounded-2xl border border-slate-200 p-4">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-semibold text-slate-900">流程文件</h3>
            <button type="button" class="btn-secondary" @click="openFiles(detail)">查看文件</button>
          </div>
          <div class="mt-3 space-y-2">
            <div v-for="file in detailFiles" :key="file.id || file.tencentFileId" class="rounded-xl bg-slate-50 p-3 text-sm">
              <div class="font-medium text-slate-900">{{ file.fileName || file.name || '-' }}</div>
              <div class="mt-1 text-xs text-slate-500">TencentFileId: {{ file.tencentFileId || '-' }}</div>
            </div>
            <p v-if="!detailFiles.length" class="text-sm text-slate-500">暂无文件信息</p>
          </div>
        </section>
      </div>
    </el-drawer>

    <el-dialog v-model="createVisible" title="发起签署流程" width="720px">
      <el-form label-width="120px">
        <el-form-item label="流程名称" required><input v-model="createForm.flowName" class="form-input w-full" /></el-form-item>
        <el-form-item label="模板 ID" required><input v-model="createForm.templateId" class="form-input w-full" /></el-form-item>
        <el-form-item label="业务类型"><input v-model="createForm.bizType" class="form-input w-full" /></el-form-item>
        <el-form-item label="业务 ID"><input v-model="createForm.bizId" class="form-input w-full" /></el-form-item>
        <el-form-item label="业务编号"><input v-model="createForm.bizNo" class="form-input w-full" /></el-form-item>
        <el-form-item label="截止时间"><el-date-picker v-model="createForm.expireTime" class="form-date w-full" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" /></el-form-item>
        <el-form-item label="UserData"><textarea v-model="createForm.userData" class="form-textarea w-full" rows="3" placeholder="可选，业务透传信息" /></el-form-item>
      </el-form>
      <template #footer>
        <button class="btn-secondary" type="button" @click="createVisible = false">取消</button>
        <button class="btn-primary ml-2" type="button" :disabled="submitting" @click="submitCreate">发起</button>
      </template>
    </el-dialog>

    <el-dialog v-model="operationVisible" :title="operationTitle" width="720px">
      <div class="space-y-4">
        <el-date-picker v-if="operationType === 'deadline'" v-model="operationForm.expireTime" class="form-date w-full" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" />
        <textarea v-if="['remind', 'release', 'cancel'].includes(operationType)" v-model="operationForm.reason" class="form-textarea w-full" rows="4" :placeholder="operationPlaceholder" />
        <div v-if="operationType === 'forward'" class="grid gap-3 md:grid-cols-2">
          <input v-model="operationForm.recipientId" class="form-input" placeholder="原 RecipientId" />
          <input v-model="operationForm.targetName" class="form-input" placeholder="接收人姓名" />
          <input v-model="operationForm.targetMobile" class="form-input" placeholder="接收人手机号" />
          <input v-model="operationForm.reason" class="form-input" placeholder="转交原因" />
        </div>
        <div v-if="operationType === 'approvers'" class="space-y-3">
          <div v-for="(approver, index) in approverRows" :key="approver.key" class="grid gap-3 rounded-2xl border border-slate-200 p-3 md:grid-cols-5">
            <input v-model="approver.roleCode" class="form-input" placeholder="角色编码" />
            <input v-model="approver.name" class="form-input" placeholder="姓名/企业名" />
            <input v-model="approver.mobile" class="form-input" placeholder="手机号" />
            <input v-model="approver.organizationName" class="form-input" placeholder="企业名称" />
            <button type="button" class="btn-danger-secondary" @click="approverRows.splice(index, 1)">删除</button>
          </div>
          <button type="button" class="btn-secondary" @click="addApprover">添加签署方</button>
        </div>
      </div>
      <template #footer>
        <button class="btn-secondary" type="button" @click="operationVisible = false">取消</button>
        <button class="btn-primary ml-2" type="button" :disabled="submitting" @click="submitOperation">确认</button>
      </template>
    </el-dialog>

    <el-dialog v-model="filesVisible" title="流程文件" width="820px">
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head"><tr><th class="data-table-th text-left">文件名</th><th class="data-table-th text-left">归档状态</th><th class="data-table-th is-center">操作</th></tr></thead>
            <tbody>
              <tr v-for="file in files" :key="file.id || file.fileUrl" class="data-table-row">
                <td class="data-table-td">{{ file.fileName || file.name || '-' }}</td>
                <td class="data-table-td"><span :class="['status-badge', statusClassByResult(file.archiveStatus || file.tencentUploadStatus)]">{{ archiveStatusText(file.archiveStatus) || file.tencentUploadStatus || '-' }}</span></td>
                <td class="data-table-td is-center">
                  <a v-if="file.archiveFileUrl || file.signedFileUrl || file.fileUrl" class="table-action-button is-primary" :href="file.archiveFileUrl || file.signedFileUrl || file.fileUrl" target="_blank" rel="noreferrer">打开</a>
                  <span v-else class="text-xs text-slate-400">无链接</span>
                </td>
              </tr>
              <tr v-if="!files.length"><td colspan="3" class="data-table-empty">暂无文件</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </el-dialog>
  </ListPageShell>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import {
  archiveEsignFlow,
  cancelEsignFlow,
  createEsignFlow,
  forwardEsignFlow,
  getEsignFlowDetail,
  getEsignFlowFiles,
  getEsignFlowPage,
  modifyEsignFlowDeadline,
  releaseEsignFlow,
  remindEsignFlow,
  supplementEsignApprovers,
  syncEsignFlow,
  syncEsignFlowComponents
} from '@/api/esign'
import {
  archiveStatusText,
  blank,
  flowStatusClass,
  flowStatusText,
  formatDateTime,
  normalizePage,
  partyStatusText,
  readArray,
  statusClassByResult
} from './esign-utils'

const loading = ref(false)
const submitting = ref(false)
const list = ref([])
const detail = ref(null)
const currentFlow = ref(null)
const files = ref([])
const detailVisible = ref(false)
const createVisible = ref(false)
const operationVisible = ref(false)
const filesVisible = ref(false)
const operationType = ref('')
const filters = reactive({ keyword: '', bizType: '', flowStatus: '', archiveStatus: '' })
const pagination = ref({ current: 1, size: 10, total: 0, pages: 1 })
const createForm = reactive({ flowName: '', templateId: '', bizType: '', bizId: '', bizNo: '', expireTime: '', userData: '' })
const operationForm = reactive({ expireTime: '', reason: '', recipientId: '', targetName: '', targetMobile: '' })
const approverRows = ref([])

const flowStatusOptions = [
  { value: 'CREATING', label: '创建中' },
  { value: 'SIGNING', label: '签署中' },
  { value: 'PARTIALLY_SIGNED', label: '部分签署' },
  { value: 'COMPLETED', label: '已完成' },
  { value: 'REJECTED', label: '已拒签' },
  { value: 'CANCELED', label: '已撤回' },
  { value: 'EXPIRED', label: '已过期' },
  { value: 'RELIEVED', label: '已解除' },
  { value: 'EXCEPTION', label: '异常' },
  { value: 'INVALID', label: '已失效' }
]

const detailParties = computed(() => readArray(detail.value?.parties || detail.value?.partyList))
const detailFiles = computed(() => readArray(detail.value?.files || detail.value?.fileList))
const summaryCards = computed(() => [
  { label: '签署中', value: list.value.filter(item => ['CREATING', 'SIGNING'].includes(item.flowStatus || item.status)).length },
  { label: '部分签署', value: list.value.filter(item => (item.flowStatus || item.status) === 'PARTIALLY_SIGNED').length },
  { label: '已完成', value: list.value.filter(item => (item.flowStatus || item.status) === 'COMPLETED').length },
  { label: '异常/失败', value: list.value.filter(item => ['EXCEPTION', 'SYNC_FAILED', 'REJECTED'].includes(item.flowStatus || item.status)).length }
])

const operationTitle = computed(() => ({
  deadline: '修改截止时间',
  remind: '发送催办',
  release: '发起解除协议',
  approvers: '补充签署方',
  forward: '转他人处理',
  cancel: '取消流程'
}[operationType.value] || '流程操作'))

const operationPlaceholder = computed(() => ({
  remind: '填写催办消息，可为空',
  release: '请填写解除原因',
  cancel: '请填写取消原因'
}[operationType.value] || '请输入原因'))

const fetchList = async () => {
  loading.value = true
  try {
    const res = await getEsignFlowPage({ current: pagination.value.current, size: pagination.value.size, ...filters })
    const page = normalizePage(res, pagination.value)
    list.value = page.records
    pagination.value = page.pagination
  } catch (error) {
    ElMessage.error(error.message || '加载签署流程失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  pagination.value.current = 1
  fetchList()
}

const handleReset = () => {
  Object.assign(filters, { keyword: '', bizType: '', flowStatus: '', archiveStatus: '' })
  handleSearch()
}

const handlePageChange = page => {
  pagination.value.current = page
  fetchList()
}

const handlePageSizeChange = size => {
  pagination.value.size = size
  pagination.value.current = 1
  fetchList()
}

const openCreate = () => {
  Object.assign(createForm, { flowName: '', templateId: '', bizType: '', bizId: '', bizNo: '', expireTime: '', userData: '' })
  createVisible.value = true
}

const submitCreate = async () => {
  if (!createForm.flowName || !createForm.templateId) {
    ElMessage.warning('请填写流程名称和模板 ID')
    return
  }
  submitting.value = true
  try {
    await createEsignFlow({ ...createForm })
    ElMessage.success('签署流程已发起')
    createVisible.value = false
    fetchList()
  } catch (error) {
    ElMessage.error(error.message || '发起流程失败')
  } finally {
    submitting.value = false
  }
}

const openDetail = async (item) => {
  detailVisible.value = true
  detail.value = null
  try {
    detail.value = await getEsignFlowDetail(item.id)
  } catch (error) {
    ElMessage.error(error.message || '加载流程详情失败')
  }
}

const runSimpleAction = async (item, type) => {
  const actionMap = {
    sync: ['同步流程状态', () => syncEsignFlow(item.id), '同步已完成'],
    components: ['同步填写控件', () => syncEsignFlowComponents(item.id), '控件同步已完成'],
    archive: ['触发流程归档', () => archiveEsignFlow(item.id), '归档任务已提交']
  }
  const [title, action, message] = actionMap[type]
  await ElMessageBox.confirm(`确认${title}「${item.flowName || item.flowNo || item.id}」吗？`, title, { type: 'warning' })
  try {
    await action()
    ElMessage.success(message)
    fetchList()
  } catch (error) {
    ElMessage.error(error.message || `${title}失败`)
  }
}

const openOperation = (item, type) => {
  currentFlow.value = item
  operationType.value = type
  Object.assign(operationForm, { expireTime: item.expireTime || '', reason: '', recipientId: '', targetName: '', targetMobile: '' })
  approverRows.value = []
  if (type === 'approvers') addApprover()
  operationVisible.value = true
}

const addApprover = () => approverRows.value.push({ key: `${Date.now()}-${Math.random()}`, roleCode: '', name: '', mobile: '', organizationName: '' })

const submitOperation = async () => {
  submitting.value = true
  try {
    const id = currentFlow.value.id
    if (operationType.value === 'deadline') await modifyEsignFlowDeadline(id, { expireTime: operationForm.expireTime })
    if (operationType.value === 'remind') await remindEsignFlow(id, { message: operationForm.reason })
    if (operationType.value === 'release') await releaseEsignFlow(id, { reason: operationForm.reason })
    if (operationType.value === 'cancel') await cancelEsignFlow(id, { reason: operationForm.reason })
    if (operationType.value === 'forward') await forwardEsignFlow(id, { ...operationForm })
    if (operationType.value === 'approvers') {
      const approvers = approverRows.value.map((item) => {
        const row = { ...item }
        delete row.key
        return row
      }).filter(row => row.roleCode || row.mobile || row.name)
      await supplementEsignApprovers(id, { approvers })
    }
    ElMessage.success('流程操作已提交')
    operationVisible.value = false
    fetchList()
  } catch (error) {
    ElMessage.error(error.message || '流程操作失败')
  } finally {
    submitting.value = false
  }
}

const openFiles = async (item) => {
  filesVisible.value = true
  files.value = []
  try {
    const res = await getEsignFlowFiles(item.id)
    files.value = readArray(res)
  } catch (error) {
    ElMessage.error(error.message || '加载流程文件失败')
  }
}

onMounted(fetchList)
</script>
